import React from 'react';

import { Button } from '../../../../../design-system/components/desktop';
import {
  DetailGallery,
  DetailList,
  DetailRow,
} from '../../../../../design-system/patterns/console';
import { AuthBanner } from '../../../../../design-system/patterns/auth';
import { formatCurrency } from '../../../../../utils/common';
import { WizardPanel } from '../../wizard';

/**
 * Step 5, review and publish. The view. Pure.
 *
 * The gallery and the fact list are `patterns/console`'s `DetailGallery` and
 * `DetailList`, the same two the admin listing drawer uses. A review screen and
 * a detail drawer are the same shape (a set of committed values with an image
 * beside them), and the pattern already solves the prev/next, the counter and
 * the empty case, so this file has none of that in it.
 *
 * The publish control was a `GradientButton`, the cyan gradient the system is
 * removing. It is the ink primary now: publishing is a COMMAND, and command is
 * ink. Its label carries the consequence, which is why it still says "Submit to
 * review" rather than "Done": a Sleevr listing is reviewed before it goes live
 * and the button should not promise otherwise.
 */
export function ReviewPublishStepView({
  formData,
  card,
  conditionLabels = {},
  images = [],
  imageIndex = 0,
  onPrevImage,
  onNextImage,
  onSubmit,
  isSubmitting = false,
  submitError,
  titleRef,
}) {
  const boughtFor =
    typeof formData.bought_for_price === 'string' ? formData.bought_for_price.trim() : '';

  return (
    <WizardPanel
      ref={titleRef}
      step={4}
      title="Review and publish"
      sub="This is the listing a buyer will see. Check the condition and the photos against the card in your hand, then send it for review."
    >
      <AuthBanner tone="error">{submitError}</AuthBanner>

      <div className="dsa-cl-review">
        <DetailGallery
          images={images}
          index={imageIndex}
          onPrev={onPrevImage}
          onNext={onNextImage}
          alt={card?.product_name ? `${card.product_name} photo` : 'Card photo'}
        />

        <DetailList>
          <DetailRow label="Card" value={card?.product_name || 'No card selected'} />
          <DetailRow label="Set" value={card?.catalog_group} />
          <DetailRow label="Card number" value={card?.card_number} />
          <DetailRow
            label="Condition"
            value={conditionLabels[formData.condition] || formData.condition}
          />
          <DetailRow label="Finish" value={formData.finish} />
          <DetailRow label="Price" value={formatCurrency(formData.price)} />
          <DetailRow label="Quantity" value={String(formData.quantity)} />
          {boughtFor ? (
            <DetailRow label="Bought for" value={formatCurrency(boughtFor)} />
          ) : null}
          {/* THESE TWO STAY, AND THEY ARE NOT LEFTOVERS. The collection step
              they used to be typed into is gone; both are now resolved from
              the seller's own profile in `CreateListing.jsx`. This is the one
              place the seller sees what is actually going on the listing
              before it is published, so removing them would mean posting two
              fields the seller was never shown. `DetailRow` drops an empty
              value, so a seller with no saved address gets no blank row —
              they get the submit guard instead. */}
          <DetailRow label="Collection address" value={formData.address} />
          <DetailRow
            label="Handling time"
            value={`${formData.processing_days} day${formData.processing_days === 1 ? '' : 's'}`}
          />
          {formData.description ? (
            <DetailRow label="Description" value={formData.description} />
          ) : null}
        </DetailList>
      </div>

      <p className="dsk-hint">
        Sleevr ships nationwide. Once this listing is approved, buyers see it in the marketplace
        and delivery is arranged at checkout.
      </p>

      <div>
        <Button variant="primary" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit to review'}
        </Button>
      </div>
    </WizardPanel>
  );
}

export default ReviewPublishStepView;
